import React, { useEffect, useRef } from 'react';
import { ChevronLeft, Send } from 'lucide-react';
import { Button } from '../ui';
import { FeedbackContext, FeedbackKind } from '../../client/feedbackClient';
import { MAX_MESSAGE_CHARS } from '../../hooks/useFeedbackForm';
import { displayRfqId } from '../../utils/rfqIdentity';

/**
 * Step two: write it and send it. One box, one button.
 *
 * The placeholder changes per kind because "Tell us more" asks nothing — the
 * prompt should already know what it is you came to say.
 *
 * The attached-context line is deliberately plain and always visible. Telling
 * someone afterwards what you collected is not the same as telling them first.
 */

const PLACEHOLDER: Record<FeedbackKind, string> = {
  PROBLEM: "What went wrong? What were you trying to do when it happened?",
  IDEA: "What would you like this to do? However half-formed — I'd rather hear it early.",
  PRAISE: "Which bit worked? Knowing what to keep is as useful as knowing what to fix.",
};

export interface FeedbackComposerProps {
  kind: FeedbackKind;
  message: string;
  context: FeedbackContext;
  sending: boolean;
  error: string | null;
  canSend: boolean;
  onMessage: (message: string) => void;
  onBack: () => void;
  onSend: () => void;
}

/** The plain-English list of what rides along, in the order it is collected. */
const attachedFacts = (context: FeedbackContext): string =>
  [
    `this page (${context.path})`,
    context.rfqId ? `the RFQ (${displayRfqId(context.rfqId)})` : null,
    'your browser and screen size',
  ]
    .filter(Boolean)
    .join(', ');

export const FeedbackComposer: React.FC<FeedbackComposerProps> = ({
  kind, message, context, sending, error, canSend, onMessage, onBack, onSend,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus once, on arrival — never again, or it would steal the caret back
  // from someone who clicked away mid-sentence.
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && canSend) {
      event.preventDefault();
      onSend();
    }
  };

  return (
    <div className="flex flex-col gap-s4">
      <button
        type="button"
        onClick={onBack}
        className="-ml-s2 inline-flex w-fit items-center gap-s1 rounded-control px-s2 py-[2px] text-meta font-medium text-ink-secondary hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-action"
      >
        <ChevronLeft className="h-[13px] w-[13px]" />
        Pick something else
      </button>

      <textarea
        ref={textareaRef}
        value={message}
        rows={5}
        maxLength={MAX_MESSAGE_CHARS}
        onChange={(event) => onMessage(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={PLACEHOLDER[kind]}
        aria-label="Your feedback"
        className="w-full resize-y rounded-control border border-line bg-surface px-s4 py-s3 text-body-md text-ink placeholder:text-ink-muted focus:border-action focus:outline-none focus:ring-2 focus:ring-action/20"
      />

      <p className="text-meta leading-[1.5] text-ink-muted">
        I'll attach {attachedFacts(context)} so I don't have to ask.
      </p>

      {error && (
        <p role="alert" className="rounded-control border border-danger-border bg-danger-bg px-s4 py-s3 text-body-sm text-danger-ink">
          {error}
        </p>
      )}

      <div className="flex items-center justify-between gap-s4">
        <span className="font-mono text-[11px] text-ink-muted" aria-hidden="true">
          {message.length}/{MAX_MESSAGE_CHARS}
        </span>
        <Button onClick={onSend} disabled={!canSend} iconRight={<Send />}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </div>
  );
};

export default FeedbackComposer;
